import React, { useState, useEffect } from 'react';
import { dashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Target,
  BarChart3,
  ShieldAlert,
  FolderKanban,
  FileText,
  TrendingUp,
  Clock,
  Printer,
  ChevronLeft,
  ChevronRight,
  WalletCards
} from 'lucide-react';
import { exportPdf, pdfStatCard, pdfTable, pdfSection } from '../utils/MintekPdfExport';

const fmtR = (v) => {
  const n = Number(v || 0);
  if (n >= 1000000) return `R${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `R${(n / 1000).toFixed(0)}K`;
  return `R${n}`;
};

const num = (v) => (v === null || v === undefined || v === '') ? '—' : v;

const pctColor = (v) =>
  v >= 0 ? 'text-emerald-700' :
  v >= -15 ? 'text-amber-700' :
  'text-[#8f1430]';

const pctBg = (v) =>
  v >= 0 ? 'bg-emerald-50 border-emerald-100' :
  v >= -15 ? 'bg-amber-50 border-amber-100' :
  'bg-[#8f1430]/10 border-[#8f1430]/20';

// Standard ERM heat colors — identical scale to the Risk module heatmap
const rlBg = (s) =>
  s <= 4 ? 'bg-gradient-to-br from-green-300 to-green-500 text-green-950' :
  s <= 8 ? 'bg-gradient-to-br from-yellow-300 to-yellow-400 text-yellow-950' :
  s <= 12 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white' :
  s <= 16 ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' :
  'bg-gradient-to-br from-red-700 to-red-900 text-white';

const EMPTY = {
  planHealth: { total: 0, atRisk: 0, onTrack: 0, completed: 0 },
  kpiSummary: { total: 0, onTrack: 0, atRisk: 0, behind: 0 },
  riskSummary: { total: 0, high: 0, aboveAppetite: 0, overdueActions: 0, opportunities: 0 },
  projectSummary: { total: 0, active: 0, totalBudget: 0, totalSpend: 0, utilisation: 0 },
  businessPlanSummary: { total: 0, draft: 0, inProgress: 0, submitted: 0, approved: 0 },
  objectives: [],
  kpiActuals: [],
  kpiConsolidation: [],
  riskHeatmap: [],
  projectPortfolio: []
};

const Card = ({ children, className = '' }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white shadow-[0_16px_45px_rgba(15,23,42,0.08)] ${className}`}>
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
    <div>
      <h3 className="flex items-center gap-2 text-[13px] font-black uppercase tracking-[0.12em] text-slate-800">
        {Icon && <Icon size={15} className="text-[#8f1430]" />}
        {title}
      </h3>
      {subtitle && <p className="mt-1 text-[11px] text-slate-500">{subtitle}</p>}
    </div>
    {right}
  </div>
);

const MiniProgress = ({ value, className = '', muted = false }) => {
  const width = Math.max(0, Math.min(Number(value || 0), 100));

  return (
    <div className={`h-2 rounded-full bg-slate-200/80 overflow-hidden ${className}`}>
      <div
        className={`h-full rounded-full ${muted ? 'bg-slate-500' : 'bg-[#8f1430]'}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
};

export default function DashboardPage() {
  const [d, setD] = useState(EMPTY);
  const [fy, setFy] = useState(2026);
  const [loading, setLoading] = useState(true);
  const [kpiPage, setKpiPage] = useState(1);
  const { user } = useAuth();

  const load = React.useCallback((showSpinner = true) => {
    if (showSpinner) { setLoading(true); setKpiPage(1); }
    dashboardApi
      .get(fy)
      .then(r => {
        setD(r.data || EMPTY);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  // Real-time freshness: silently refetch whenever the user returns to this
  // tab/window (e.g. after editing data in another module or browser tab).
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') load(false); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [load]);

  const exportDashboardPdf = () => {
    const html = `
      ${pdfSection('Strategic Plan Health')}
      <div style="display:flex;gap:12px;margin-bottom:16px">
        ${pdfStatCard('Total SOs', d.planHealth.total)}
        ${pdfStatCard('At Risk', d.planHealth.atRisk)}
        ${pdfStatCard('On Track', d.planHealth.onTrack)}
        ${pdfStatCard('Achieved', d.planHealth.completed)}
      </div>

      ${pdfSection('Key Metrics')}
      <div style="display:flex;gap:12px;margin-bottom:16px">
        ${pdfStatCard('KPIs On Track', d.kpiSummary.onTrack + '/' + d.kpiSummary.total)}
        ${pdfStatCard('Active Projects', d.projectSummary.active)}
        ${pdfStatCard('High Risks', d.riskSummary.high)}
        ${pdfStatCard('Overdue Actions', d.riskSummary.overdueActions)}
      </div>

      ${pdfSection('Strategic Objectives')}
      ${pdfTable(['Ref','Objective','Progress','KPIs','Programmes','Risks'],
        d.objectives.map(o => [
          o.reference,
          o.title,
          o.progressPercentage + '%',
          o.kpiCount,
          o.initiativeCount,
          o.riskCount
        ]))}

      ${pdfSection('KPI Performance — Org vs Divisional vs Actual')}
      ${pdfTable(['Ref','KPI','Org Target','Div Total','Divs','Actual','Var %'],
        ((d.kpiConsolidation && d.kpiConsolidation.length) ? d.kpiConsolidation : d.kpiActuals).map(kpi => {
          const a = (kpi.actuals || [])[0];
          const target = kpi.latestTargetValue ?? a?.targetValue ?? kpi.orgAnnualTarget ?? '—';
          const _cap = (kpi.actuals || []).filter(x => x.actualValue != null);
          const _pct = String(kpi.unit || '').toLowerCase().includes('%');
          const actual = _cap.length === 0
            ? (kpi.latestActualValue ?? a?.actualValue ?? '—')
            : (_pct ? _cap[_cap.length - 1].actualValue : _cap.reduce((t, x) => t + Number(x.actualValue || 0), 0));
          const variance = kpi.latestVariancePercentage ?? a?.variancePercentage;

          return [
            kpi.reference,
            kpi.name,
            kpi.orgAnnualTarget ?? target,
            kpi.totalDivisionalAnnual ?? '—',
            kpi.divisionsContributing ?? '—',
            actual,
            variance != null ? variance.toFixed(1) + '%' : 'Pending'
          ];
        }))}

      ${pdfSection('Portfolio Budget')}
      <div style="display:flex;gap:12px;margin-bottom:16px">
        ${pdfStatCard('Total Budget', fmtR(d.projectSummary.totalBudget))}
        ${pdfStatCard('Spent', fmtR(d.projectSummary.totalSpend))}
        ${pdfStatCard('Utilisation', (d.projectSummary.utilisation || 0).toFixed(1) + '%')}
      </div>
    `;

    exportPdf('Executive Dashboard Report', 'FY ' + (fy - 1) + '/' + (fy % 100), html);
  };

  const planTotal = Math.max(Number(d.planHealth.total || 0), 1);
  const onTrackPct = (Number(d.planHealth.onTrack || 0) / planTotal) * 100;
  const atRiskPct = (Number(d.planHealth.atRisk || 0) / planTotal) * 100;
  const completedPct = (Number(d.planHealth.completed || 0) / planTotal) * 100;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-pulse text-sm font-semibold text-slate-400">
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-5 bg-[#f4f5f7] p-4 text-slate-900 xl:p-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <LayoutDashboard size={28} className="text-[#8f1430]" />
          <div>
            <h1 className="text-2xl font-black tracking-tight text-[#8f1430]">
              Executive Dashboard
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              FY {fy - 1}/{fy % 100} · Welcome back, {user?.firstName || 'User'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            className="rounded-xl border border-slate-200 bg-[#e7d8dc] px-3 py-2 text-xs font-bold text-[#8f1430] outline-none"
            value={fy}
            onChange={e => setFy(+e.target.value)}
          >
            <option value={2025}>FY 24/25</option>
            <option value={2026}>FY 25/26</option>
            <option value={2027}>FY 26/27</option>
          </select>

          <button
            onClick={exportDashboardPdf}
            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-700 px-4 py-2 text-xs font-black text-white hover:bg-slate-800"
          >
            <Printer size={14} />
            PDF Report
          </button>
        </div>
      </div>

      {/* Strategic Plan Health */}
      <div className="rounded-2xl bg-[#1d222b] p-5 text-white shadow-[0_18px_45px_rgba(15,23,42,0.20)]">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[13px] font-black uppercase tracking-[0.14em]">
            Strategic Plan Health - Performance Tracking
          </h2>
          <span className="text-xs text-white/70">
            {d.planHealth.total} Strategic Objectives
          </span>
        </div>

        <div className="flex h-8 overflow-hidden rounded-xl bg-slate-500/50">
          {completedPct > 0 && (
            <div
              className="flex items-center justify-center bg-slate-600 text-[11px] font-black"
              style={{ width: `${completedPct}%` }}
            >
              {d.planHealth.completed} Achieved
            </div>
          )}

          {onTrackPct > 0 && (
            <div
              className="flex items-center justify-center bg-slate-400 text-[11px] font-black text-white"
              style={{ width: `${onTrackPct}%` }}
            >
              {d.planHealth.onTrack} On Track
            </div>
          )}

          {atRiskPct > 0 && (
            <div
              className="flex items-center justify-center bg-[#8f1430] text-[11px] font-black"
              style={{ width: `${atRiskPct}%` }}
            >
              {d.planHealth.atRisk} At Risk
            </div>
          )}

          {d.planHealth.total === 0 && (
            <div className="flex flex-1 items-center justify-center text-xs text-white/60">
              No data
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: 'KPIs on Track',
            value: `${d.kpiSummary.onTrack}/${d.kpiSummary.total}`,
            sub: `${d.kpiSummary.behind} behind`,
            Icon: BarChart3,
            bars: 3
          },
          {
            label: 'Active Projects',
            value: d.projectSummary.active,
            sub: `${fmtR(d.projectSummary.totalBudget)} budget`,
            Icon: FolderKanban,
            bars: 3
          },
          {
            label: 'High Risks',
            value: d.riskSummary.high,
            sub: `${d.riskSummary.aboveAppetite} above appetite`,
            Icon: ShieldAlert,
            bars: 4
          },
          {
            label: 'Overdue Actions',
            value: d.riskSummary.overdueActions,
            sub: `${d.riskSummary.total} total risks`,
            Icon: Clock,
            bars: 0
          }
        ].map((c) => (
          <Card key={c.label} className="border-[#8f1430]/70 p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-800">
                  {c.label}
                </p>

                <div className="mt-1 flex items-center gap-3">
                  <p className="text-3xl font-black text-[#8f1430]">
                    {c.value}
                  </p>

                  {c.bars > 0 && (
                    <div className="flex gap-1">
                      {Array.from({ length: c.bars }).map((_, i) => (
                        <span
                          key={i}
                          className={`h-5 w-14 rounded ${
                            c.label === 'High Risks' ? 'bg-[#8f1430]' : 'bg-slate-500'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <p className="mt-1 text-xs font-medium text-slate-600">
                  {c.sub}
                </p>
              </div>

              <c.Icon size={18} className="text-[#8f1430]/80" />
            </div>
          </Card>
        ))}
      </div>

      {/* Strategic Objectives */}
      <Card className="overflow-hidden">
        <SectionTitle icon={Target} title="Strategic Objectives" />

        <div className="grid grid-cols-1 gap-3 px-5 pb-5 md:grid-cols-2 xl:grid-cols-3">
          {(d.objectives || []).map(so => (
            <div
              key={so.id}
              className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="shrink-0 rounded bg-[#8f1430]/10 px-2 py-0.5 font-mono text-[10px] font-black text-[#8f1430]">
                    {so.reference}
                  </span>
                  <span className="truncate text-sm font-bold text-slate-800">
                    {so.title}
                  </span>
                </div>

                <span className="shrink-0 text-sm font-black text-[#8f1430]">
                  {so.progressPercentage}%
                </span>
              </div>

              <MiniProgress value={so.progressPercentage} />

              <div className="mt-2 flex gap-3 text-[10px] font-medium text-slate-500">
                <span>{so.kpiCount} KPIs</span>
                <span>{so.initiativeCount} Programmes</span>
                <span>{so.riskCount} Risks</span>
              </div>
            </div>
          ))}

          {(d.objectives || []).length === 0 && (
            <p className="col-span-full py-6 text-center text-sm text-slate-400">
              No strategic objectives
            </p>
          )}
        </div>
      </Card>

      {/* KPI Performance with 8-row pager */}
      {(() => {
        const cons = d.kpiConsolidation || [];
        const acts = d.kpiActuals || [];

        const merged = (cons.length ? cons : acts).map(kpi => {
          const fromActuals = acts.find(a => a.id === kpi.id) || kpi;
          const allActuals = (kpi.actuals && kpi.actuals.length ? kpi.actuals : fromActuals.actuals) || [];
          const a = allActuals[0] || null;

          // YTD actual: cumulative across captured quarters for additive units
          // (Count, R million, etc.). Percentage/ratio KPIs are not additive —
          // use the latest captured quarter instead.
          const unitStr = String(kpi.unit || '').toLowerCase();
          const isPercent = unitStr.includes('%') || unitStr.includes('percent') || unitStr.includes('ratio');
          const captured = allActuals.filter(x => x.actualValue != null);
          const latestVal = kpi.latestActualValue ?? a?.actualValue ?? null;
          const actual = captured.length === 0
            ? latestVal
            : (isPercent
                ? captured[captured.length - 1].actualValue           // latest quarter
                : captured.reduce((t, x) => t + Number(x.actualValue || 0), 0)); // YTD sum
          const variance = kpi.latestVariancePercentage ?? a?.variancePercentage ?? null;
          const approval = kpi.latestApprovalStatus ?? a?.approvalStatus ?? null;
          const orgTarget = kpi.orgAnnualTarget ?? null;
          const divTotal = kpi.totalDivisionalAnnual ?? 0;
          // De-duplicate divisional targets: one chip per division (duplicate
          // allocation rows in the data would otherwise render twice).
          const divs = Object.values(
            (kpi.divisionalTargets || []).reduce((acc, dt) => {
              const k = dt.divisionId || dt.divisionName;
              if (!acc[k]) acc[k] = dt;
              return acc;
            }, {})
          );
          const divCount = kpi.divisionsContributing ?? divs.length;

          const achievement =
            orgTarget && orgTarget > 0 && actual != null
              ? Math.min((Number(actual) / Number(orgTarget)) * 100, 150)
              : null;

          const coverage =
            orgTarget && orgTarget > 0
              ? Math.min((Number(divTotal) / Number(orgTarget)) * 100, 150)
              : null;

          return {
            kpi,
            a,
            actual,
            variance,
            approval,
            orgTarget,
            divTotal,
            divCount,
            divs,
            achievement,
            coverage
          };
        });

        const pageSize = 8;
        const totalPages = Math.max(1, Math.ceil(merged.length / pageSize));
        const safePage = Math.min(kpiPage, totalPages);
        const start = (safePage - 1) * pageSize;
        const rows = merged.slice(start, start + pageSize);

        const totalCount = merged.length;
        const onTrackCount = merged.filter(m => m.variance != null && m.variance >= 0).length;
        const behindCount = merged.filter(m => m.variance != null && m.variance < -15).length;
        const pendingCount = merged.filter(m => m.variance == null).length;
        const fullyCovered = merged.filter(m => m.coverage != null && m.coverage >= 99).length;

        return (
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-3">
              <div>
                <h3 className="flex items-center gap-2 text-[13px] font-black uppercase tracking-[0.12em] text-slate-800">
                  <BarChart3 size={15} className="text-[#8f1430]" />
                  KPI Performance — Org vs Divisional vs Actual
                </h3>
                <p className="mt-1 text-[11px] text-slate-500">
                  Annual targets, divisional correlations, latest actuals and variances — all in one view
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {[
                  `${totalCount} KPIs`,
                  `${onTrackCount} on track`,
                  `${behindCount} behind`,
                  `${pendingCount} pending`,
                  `${fullyCovered}/${totalCount || 0} fully allocated to divisions`
                ].map((s, i) => (
                  <span
                    key={s}
                    className={`rounded-md px-2 py-1 text-[10px] font-black ${
                      i === 1 ? 'bg-slate-700 text-white' :
                      i === 2 ? 'bg-[#8f1430] text-white' :
                      'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-[11px]">
                <thead>
                  <tr className="border-y border-slate-200 bg-slate-300/80 text-slate-800">
                    <th className="w-[34%] px-4 py-3 text-left font-black uppercase tracking-wider">KPI</th>
                    <th className="w-[7%] px-3 py-3 text-right font-black uppercase tracking-wider">Org Target</th>
                    <th className="w-[7%] px-3 py-3 text-right font-black uppercase tracking-wider">Div Total</th>
                    <th className="w-[12%] px-3 py-3 text-left font-black uppercase tracking-wider">Coverage</th>
                    <th className="w-[7%] px-3 py-3 text-right font-black uppercase tracking-wider">Actual</th>
                    <th className="w-[12%] px-3 py-3 text-left font-black uppercase tracking-wider">Achievement</th>
                    <th className="w-[7%] px-3 py-3 text-right font-black uppercase tracking-wider">Var %</th>
                    <th className="w-[14%] px-3 py-3 text-left font-black uppercase tracking-wider">Divisional Breakdown</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map(m => {
                    const { kpi } = m;

                    const divPalette = [
                      'bg-[#8f1430]',
                      'bg-slate-600',
                      'bg-[#b67b88]',
                      'bg-slate-400',
                      'bg-[#5e6673]',
                      'bg-[#704050]'
                    ];

                    const divSegments = (m.divs || [])
                      .map((dt, i) => ({
                        name: dt.divisionName,
                        val: Number(dt.divisionalAnnualTarget || 0),
                        color: divPalette[i % divPalette.length]
                      }))
                      .filter(s => s.val > 0);

                    const segTotal = divSegments.reduce((s, x) => s + x.val, 0);

                    return (
                      <tr key={kpi.id} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-3 align-top">
                          <span className="block font-mono text-[10px] font-black text-[#8f1430]">
                            {kpi.reference}
                          </span>
                          <span
                            className="block truncate text-xs font-bold text-slate-800"
                            title={kpi.name}
                          >
                            {kpi.name}
                          </span>
                          {kpi.unit && (
                            <span className="mt-0.5 block text-[10px] text-slate-500">
                              Unit: {kpi.unit}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-3 text-right align-top font-mono font-black text-slate-900">
                          {num(m.orgTarget)}
                        </td>

                        <td className="px-3 py-3 text-right align-top font-mono font-black text-[#8f1430]">
                          {m.divTotal || '—'}
                        </td>

                        <td className="px-3 py-3 align-top">
                          {m.coverage != null ? (
                            <>
                              <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-200 ring-1 ring-slate-300">
                                {segTotal > 0 ? (
                                  divSegments.map((s, i) => (
                                    <div
                                      key={i}
                                      className={`${s.color} h-full`}
                                      style={{ width: `${(s.val / Math.max(m.orgTarget, segTotal)) * 100}%` }}
                                      title={`${s.name}: ${s.val}`}
                                    />
                                  ))
                                ) : (
                                  <div
                                    className="h-full bg-slate-500"
                                    style={{ width: `${m.coverage}%` }}
                                  />
                                )}
                              </div>

                              <div className="mt-1 flex justify-between text-[9px] font-bold text-slate-500">
                                <span>{m.divCount} division{m.divCount === 1 ? '' : 's'}</span>
                                <span>{m.coverage.toFixed(0)}%</span>
                              </div>
                            </>
                          ) : (
                            <span className="text-[10px] text-slate-400">—</span>
                          )}
                        </td>

                        <td className="px-3 py-3 text-right align-top font-mono font-black text-slate-900">
                          {num(m.actual)}
                        </td>

                        <td className="px-3 py-3 align-top">
                          {m.achievement != null ? (
                            <>
                              <div className="relative h-2.5 overflow-hidden rounded-full bg-slate-200 ring-1 ring-slate-300">
                                <div
                                  className="absolute inset-y-0 w-px bg-slate-600"
                                  style={{ left: `${(100 / 150) * 100}%` }}
                                />
                                <div
                                  className="h-full rounded-full bg-[#8f1430]"
                                  style={{ width: `${(m.achievement / 150) * 100}%` }}
                                />
                              </div>

                              <div className="mt-1 flex justify-between text-[9px] font-bold text-slate-500">
                                <span>vs target</span>
                                <span>{m.achievement.toFixed(0)}%</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="h-2.5 flex-1 rounded-full bg-slate-200 ring-1 ring-slate-300" />
                              <span className="text-[10px] text-slate-500">Pending</span>
                            </div>
                          )}
                        </td>

                        <td className="px-3 py-3 text-right align-top">
                          {m.variance != null ? (
                            <span className={`inline-flex rounded border px-2 py-1 text-[10px] font-black ${pctBg(m.variance)} ${pctColor(m.variance)}`}>
                              {m.variance > 0 ? '+' : ''}{m.variance.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-500">Pending</span>
                          )}
                        </td>

                        <td className="px-3 py-3 align-top">
                          <div className="flex max-w-full flex-wrap gap-1">
                            {(m.divs || []).slice(0, 4).map((dt, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 rounded bg-[#8f1430]/10 px-1.5 py-0.5 text-[9px] font-bold text-slate-700"
                              >
                                <span className={`h-1.5 w-1.5 rounded-full ${divPalette[i % divPalette.length]}`} />
                                <span className="truncate max-w-[72px]">{dt.divisionName}</span>
                                <strong>{dt.divisionalAnnualTarget ?? '—'}</strong>
                              </span>
                            ))}

                            {(m.divs || []).length > 4 && (
                              <span className="text-[9px] font-bold text-slate-500">
                                +{m.divs.length - 4}
                              </span>
                            )}

                            {(m.divs || []).length === 0 && m.divCount > 0 && (
                              <span className="text-[10px] italic text-amber-700">
                                {m.divCount} division target{m.divCount === 1 ? '' : 's'}
                              </span>
                            )}

                            {(m.divs || []).length === 0 && m.divCount === 0 && (
                              <span className="text-[10px] italic text-slate-400">
                                Not yet allocated to divisions
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {merged.length === 0 && (
                <p className="py-8 text-center text-sm text-slate-400">
                  No KPIs configured for this financial year
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <p className="text-xs font-semibold text-slate-500">
                Showing {merged.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, merged.length)} of {merged.length} KPIs · 8 per page
              </p>

              <div className="flex items-center gap-2">
                <button
                  disabled={safePage <= 1}
                  onClick={() => setKpiPage(p => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>

                <span className="rounded-lg bg-[#8f1430] px-3 py-1.5 text-xs font-black text-white">
                  Page {safePage} of {totalPages}
                </span>

                <button
                  disabled={safePage >= totalPages}
                  onClick={() => setKpiPage(p => Math.min(totalPages, p + 1))}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Risk Heatmap + Budget */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-4 flex items-center gap-2 text-[13px] font-black uppercase tracking-[0.12em] text-slate-800">
            <ShieldAlert size={15} className="text-[#8f1430]" />
            Residual Risk Heatmap
          </h3>

          <div className="grid grid-cols-6 gap-1.5">
            <div />

            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="py-1 text-center text-[10px] font-black text-slate-500">
                L{i}
              </div>
            ))}

            {[5, 4, 3, 2, 1].map(impact => (
              <React.Fragment key={impact}>
                <div className="flex items-center justify-center text-[10px] font-black text-slate-500">
                  I{impact}
                </div>

                {[1, 2, 3, 4, 5].map(likelihood => {
                  const score = likelihood * impact;
                  const cellRisks = (d.riskHeatmap || []).filter(
                    r => r.residualLikelihood === likelihood && r.residualImpact === impact
                  );

                  return (
                    <div
                      key={`${likelihood}-${impact}`}
                      className={`${rlBg(score)} group relative flex min-h-[38px] items-center justify-center rounded-lg text-xs font-black`}
                    >
                      {cellRisks.length > 0 ? cellRisks.length : (
                        <span className="opacity-80">{score}</span>
                      )}

                      {cellRisks.length > 0 && (
                        <div className="absolute bottom-full left-1/2 z-20 mb-1 hidden w-52 -translate-x-1/2 rounded-lg bg-slate-900 p-2 text-[9px] font-medium text-white shadow-xl group-hover:block">
                          {cellRisks.map(r => (
                            <div key={r.id}>
                              <span className="font-mono text-red-200">{r.reference}</span> {r.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 flex items-center gap-2 text-[13px] font-black uppercase tracking-[0.12em] text-slate-800">
            <WalletCards size={15} className="text-[#8f1430]" />
            Portfolio Budget
          </h3>

          <div className="mb-5 flex flex-wrap items-center gap-8">
            <div>
              <p className="text-[11px] font-black uppercase text-slate-400">Total Budget</p>
              <p className="text-3xl font-black text-slate-800">
                {fmtR(d.projectSummary.totalBudget)}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-black uppercase text-slate-400">Spent</p>
              <p className="text-3xl font-black text-[#8f1430]">
                {fmtR(d.projectSummary.totalSpend)}
              </p>
            </div>

            <div>
              <p className="text-[11px] font-black uppercase text-slate-400">Utilisation</p>
              <p className="text-3xl font-black text-[#8f1430]">
                {(d.projectSummary.utilisation || 0).toFixed(1)}%
              </p>
            </div>
          </div>

          <MiniProgress
            value={Math.min(d.projectSummary.utilisation || 0, 100)}
            className="h-6"
          />

          <h4 className="mt-6 mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-700">
            <FileText size={14} className="text-[#8f1430]" />
            Business Plan Status
          </h4>

          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {[
              { l: 'Draft', v: d.businessPlanSummary.draft, c: 'bg-[#1f2937] text-white' },
              { l: 'In Progress', v: d.businessPlanSummary.inProgress, c: 'bg-slate-600 text-white' },
              { l: 'Submitted', v: d.businessPlanSummary.submitted, c: 'bg-slate-200 text-slate-800' },
              { l: 'Approved', v: d.businessPlanSummary.approved, c: 'bg-[#445b56] text-white' }
            ].map(s => (
              <div key={s.l} className={`rounded-xl p-4 text-center ${s.c}`}>
                <p className="text-3xl font-black">{s.v}</p>
                <p className="mt-1 text-[10px] font-black uppercase tracking-wider">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Project Portfolio Overview */}
      <Card className="overflow-hidden">
        <SectionTitle icon={TrendingUp} title="Project Portfolio Overview" />

        <div className="grid grid-cols-1 gap-3 px-5 pb-5 md:grid-cols-2 xl:grid-cols-3">
          {(d.projectPortfolio || []).slice(0, 9).map(p => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-1 flex items-center gap-2">
                <span className="font-mono text-[10px] font-black text-[#8f1430]">
                  {p.reference}
                </span>

                <span
                  className={`rounded-full px-2 py-0.5 text-[9px] font-black ${
                    p.status === 'InProgress' || p.status === 'Active'
                      ? 'bg-slate-700 text-white'
                      : p.status === 'Completed'
                        ? 'bg-[#445b56] text-white'
                        : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {p.status}
                </span>
              </div>

              <p className="mb-2 truncate text-sm font-bold text-slate-800">
                {p.title}
              </p>

              <div className="mb-1 flex justify-between text-[10px]">
                <span className="text-slate-500">{p.division || '—'}</span>
                <span className="font-black text-[#8f1430]">
                  {(p.progressPercentage || 0).toFixed(0)}%
                </span>
              </div>

              <MiniProgress value={p.progressPercentage || 0} />
            </div>
          ))}

          {(d.projectPortfolio || []).length === 0 && (
            <p className="col-span-full py-6 text-center text-sm text-slate-400">
              No portfolio projects
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}